/**
 * @fileoverview Component to select and render a given verification method's
 * verification flow for the verifying entity step of the Listings Launch flow.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */

import {ExternalAccountsContext}
  from '/js/yext/externalaccounts/shared/listingslaunch/externalaccountscontext';
import {EmailSelection, SendCodeToEmailButton} from '/src/com/yext/listingslaunchstorm/js/components/verification/emailselection';
import {VerificationVideoTips} from '/src/com/yext/listingslaunchstorm/js/components/verification/verificationvideotips';
import {EntityInfo} from '/src/com/yext/listingslaunchstorm/js/data/entityinfo';
import {RequestVerificationFromPublisherRequest} from '/src/com/yext/listingslaunchstorm/js/data/requestverificationfrompublisherrequest';
import {VerificationMethod} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import requestVerificationFromPublisher from '/src/com/yext/listingslaunchstorm/js/datasource/requestverificationfrompublisher';
import {Button} from '/ui/components/button/button';
import {Link} from '/ui/components/link/link';
import {useIsSmallViewport} from '/ui/lib/responsivity';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/verifyentity.module.scss';

const GOOGLE_BUSINESS_PROFILE_PUBLISHER_ID = 715;

/**
 * @param {{
 *  entity: !EntityInfo,
 *  externalId: !string,
 *  groupId: !string,
 *  verificationMethod: VerificationMethod,
 *  verificationDestination: string,
 *  setVerificationDestination: function(VerificationMethod, string): void,
 *  usernameEditable: boolean,
 *  onDidSend: function(): void,
 * }} props
 * @return {React.ReactNode}
 */
const VerificationFlow = ({
  entity,
  externalId,
  groupId,
  verificationMethod,
  verificationDestination,
  setVerificationDestination,
  usernameEditable,
  onDidSend,
}) => {
  const setMethodVerificationDestination = method => {
    const setDestinationForMethod = destination => setVerificationDestination(method, destination);
    return setDestinationForMethod;
  };

  switch (verificationMethod) {
    case VerificationMethod.SMS:
    case VerificationMethod.PHONE_CALL:
      return <GenericVerification
        entityId={entity.id}
        externalId={externalId}
        groupId={groupId}
        key={verificationMethod}
        verificationMethod={verificationMethod}
        verificationDestination={verificationDestination}
        onDidSend={onDidSend}
      />;
    case VerificationMethod.EMAIL:
      return <EmailVerification
        entityId={entity.id}
        externalId={externalId}
        groupId={groupId}
        key={verificationMethod}
        verificationMethod={verificationMethod}
        verificationDestination={verificationDestination}
        setEmailDestination={setMethodVerificationDestination(VerificationMethod.EMAIL)}
        usernameEditable={usernameEditable}
        onDidSend={onDidSend}
      />;
    case VerificationMethod.VIDEO:
      return <VideoVerification externalId={externalId} onDidSend={onDidSend} />;
    case VerificationMethod.POSTCARD:
      return <PostcardVerification address={verificationDestination} onDidSend={onDidSend} />;
    default:
      return <></>;
  }
};

/**
 * @param {{
 *  entityId: !number,
 *  externalId: !string,
 *  groupId: !string,
 *  verificationMethod: VerificationMethod,
 *  verificationDestination: !string,
 *  onDidSend: function(): void,
 * }} props
 * @return {React.ReactNode}
 */
const GenericVerification = ({
  entityId,
  externalId,
  groupId,
  verificationMethod,
  verificationDestination,
  onDidSend,
}) => {
  const {publisherIdToExternalAccount} = React.useContext(ExternalAccountsContext);
  const linkedAccount = publisherIdToExternalAccount.get(GOOGLE_BUSINESS_PROFILE_PUBLISHER_ID);
  const isSmallViewport = useIsSmallViewport();

  return (
    <div>
      <p>{yext.msg('A code will be sent to: {0}.', verificationDestination)}</p>
      <Button
        pendoId="listings-launch-flow-verifying-send-code"
        context="primary"
        fullWidth={isSmallViewport}
        onClick={() => {
          requestVerificationFromPublisher(new RequestVerificationFromPublisherRequest({
            entityId: entityId,
            externalId: externalId,
            linkedAccountId: linkedAccount.id,
            groupId: groupId,
            method: verificationMethod,
            address: verificationMethod === VerificationMethod.POSTCARD ? verificationDestination : undefined,
            phone: (verificationMethod === VerificationMethod.PHONE_CALL || verificationMethod === VerificationMethod.SMS) ? verificationDestination : undefined,
            email: verificationMethod === VerificationMethod.EMAIL ? verificationDestination : undefined,
          }), new AbortController().signal);
          onDidSend();
        }}
      >
        {yext.msg('Send Code')}
      </Button>
    </div>
  );
};

/**
 * @param {{
 *  externalId: !string,
 *  onDidSend: function(): void,
 * }} props
 * @return {React.ReactNode}
 */
const VideoVerification = ({externalId, onDidSend}) => {
  const googleMyBusinessProfileLaunchVideoLink = 'https://business.google.com/n/' + externalId + '/searchprofile?#mpd=~' + externalId + '/setup/verify';

  const isSmalllViewport = useIsSmallViewport();
  const [showTips, setShowTips] = React.useState(!isSmalllViewport);

  // add an onClick to the Link component to detect when to change the text to "Launch Again".
  // The Link UX component passes all additional props to the `a` tag inside.
  const getLink = fullWidth => (
    <Link
      pendoId="listings-launch-flow-verifying-launch-video"
      href={googleMyBusinessProfileLaunchVideoLink}
      onClick={onDidSend}
      external={true}
      context="primary"
      fullWidth={fullWidth}
    >
      {yext.msg('Launch')}
    </Link>
  );

  return (
    <div>
      <p>{yext.msg('To complete video verification, click the link below to open Google in a new tab. Afterwards, return to this screen to finish the rest of the listings launch process.')}</p>
      {isSmalllViewport ? (
        <>
          <div className={styles.verificationFlowVideoTipsAction}>
            <Button
              chromeless
              onClick={() => setShowTips(prev => !prev)}
            >
              {showTips ? yext.msg('Hide Verification Tips') : yext.msg('Show Verification Tips')}
            </Button>
          </div>
          {showTips && (<VerificationVideoTips />)}
          {getLink(true)}
        </>
      ) : (
        <>
          {getLink(false)}
          <VerificationVideoTips />
        </>
      )}
    </div>
  );
};

/**
 * @param {{
 *  address: !string,
 *  onDidSend: function(): void,
 * }} props
 * @return {React.ReactNode}
 */
const PostcardVerification = ({address, onDidSend}) => {
  const [postcardSent, setPostcardSent] = React.useState(false);
  const isSmallViewport = useIsSmallViewport();

  return (
    <div>
      {!postcardSent ? (
        <div>
          <p>{yext.msg('A postcard with your verification code will be mailed to {0}.', address)}</p>
          <p>{yext.msg('Once you receive the postcard, you will return to this process to input the code and finalize your verification. Until then, you should continue through the remainder of this process.')}</p>
          <Button
            pendoId="listings-launch-flow-verifying-send-code"
            context="default"
            fullWidth={isSmallViewport}
            onClick={() => setPostcardSent(true)}
          >
            {yext.msg('Mail Code')}
          </Button>
        </div>
      ) : (
        <div>
          <p>{yext.msg('A postcard will be mailed to {0}.', address)}</p>
          <p>{yext.msg('Continue through the rest of the listings launch process. Once you receive the postcard, you will return to this process to input the code and finalize your verification.')}</p>
          <Button
            context="default"
            fullWidth={isSmallViewport}
            onClick={onDidSend}
          >
            {yext.msg('I have a code')}
          </Button>
        </div>
      )}
    </div>
  );
};


/**
 * @param {{
 *  entityId: !number,
 *  externalId: !string,
 *  groupId: !string,
 *  verificationMethod: VerificationMethod,
 *  verificationDestination: !string,
 *  setEmailDestination: function(string): void,
 *  usernameEditable: boolean,
 *  onDidSend: function(): void,
 * }} props
 * @return {React.ReactNode}
 */
const EmailVerification = ({
  entityId,
  externalId,
  groupId,
  verificationMethod,
  verificationDestination,
  setEmailDestination,
  usernameEditable,
  onDidSend,
}) => {
  const [useAltEmail, setUseAltEmail] = React.useState(false);
  const [finalEmail, setFinalEmail] = React.useState(verificationDestination);
  const {publisherIdToExternalAccount} = React.useContext(ExternalAccountsContext);
  const linkedAccount = publisherIdToExternalAccount.get(715);
  const emailAddressTokens = verificationDestination.split('@');
  const emailDomain = emailAddressTokens.length !== 0
    ? emailAddressTokens[emailAddressTokens.length - 1] : undefined;

  function requestEmailVerificationFromPublisher(email) {
    requestVerificationFromPublisher(new RequestVerificationFromPublisherRequest({
      entityId: entityId,
      externalId: externalId,
      linkedAccountId: linkedAccount.id,
      groupId: groupId,
      method: verificationMethod,
      address: undefined,
      phone: undefined,
      email: email,
    }), new AbortController().signal);
  }

  return (
    <div>
      {usernameEditable && emailDomain
        && <EmailSelection
          emailDestination={verificationDestination}
          emailDomain={emailDomain}
          useAltEmail={useAltEmail}
          setUseAltEmail={setUseAltEmail}
          setEmailDestination={setFinalEmail}
        />}
      <SendCodeToEmailButton
        emailDestination={finalEmail}
        usernameEditable={usernameEditable}
        requestVerificationFromPublisher={requestEmailVerificationFromPublisher}
        onDidSend={onDidSend}
        setEmailDestination={setEmailDestination}
      />
    </div>
  );
};

export default VerificationFlow;
